import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { NUMERATION_SCHEMA_ENDPOINT } from '../../../../consts';
import { NumerationSchema } from '../../../../types/numerationSchemas';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from '../../../../components/ui/detailView';
import { TENANT_ROUTES } from '../../tenantConfig.routes';

export const TenantNumerationSchemaDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useCRUD(NUMERATION_SCHEMA_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [schema, setSchema] = useState<NumerationSchema | null>(null);
  const canViewSchemas = useHasPermission("settlement.view_numerationschema");

  useEffect(() => {
    const fetchSchema = async () => {
      if (id) {
        const result = await get(id);
        if (result?.success && result?.data) {
          setSchema(result.data);
          setOverride(TENANT_ROUTES.numerationSchema(id), result.data.name || '');
        } else {
          navigate(TENANT_ROUTES.numbering);
        }
      }
    };

    const handleSchemaUpdated = () => {
      fetchSchema();
    };

    window.addEventListener('numerationSchemasUpdated', handleSchemaUpdated);
    fetchSchema();
    return () => {
      window.removeEventListener('numerationSchemasUpdated', handleSchemaUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewSchemas) {
    return null;
  }

  if (!schema) {
    return <DetailPage loading />;
  }

  return (
    <DetailPage>
      <DetailHeader
        icon={<FormatListNumberedOutlinedIcon />}
        title={schema.name}
        subtitle={schema.template}
      />

      <DetailSection title={t('Basic Information')}>
        <DetailRow label={t('Name')} value={schema.name} />
        <DetailRow label={t('Template')}>
          {schema.template && (
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace' }}>
              {schema.template}
            </Typography>
          )}
        </DetailRow>
      </DetailSection>

      <DetailMetadata createdAt={schema.created_at} updatedAt={schema.updated_at} />
    </DetailPage>
  );
};
